import React, { useEffect, useState, useMemo } from "react";
import { api, formatINR, downloadReport } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, FileSpreadsheet, FileText, Info, Boxes, Settings, Search, ArrowDownAZ, Layers, Upload, Download, Warehouse as WarehouseIcon, ShieldAlert, ChevronDown, AlertTriangle } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { SearchableSelect } from "@/components/SearchableSelect";
import { BulkAssignWarehouseModal } from "@/components/BulkAssignWarehouseModal";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";

const ENTRY_TYPES = ["IN", "ISSUE", "RETURN", "TRANSFER", "SCRAP"];

export default function Inventory() {
  useUserDirectory();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState([]);
  const [stockByWh, setStockByWh] = useState([]);   // Sprint 4.3 — warehouse-rolled-up stock
  const [batches, setBatches] = useState([]);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filterBranch, setFilterBranch] = useState("");
  // Sprint 4.3 — Warehouse filter (branch-scoped). Empty = all WHs the user can see.
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [includeDepleted, setIncludeDepleted] = useState(false);
  const [batchServerTotal, setBatchServerTotal] = useState(0);
  // Common search + category filter (drive all 3 tabs)
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  // Sprint 4.7 — warehouse-coverage banner + bulk-assign overlay
  const [coverage, setCoverage] = useState(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  async function downloadStockTemplate() {
    try {
      const r = await api.get("/stock-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "stock_master_template.xlsx";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download template");
    }
  }

  async function uploadStockFile() {
    if (!bulkFile) { toast.error("Pick an Excel file first"); return; }
    setBulkBusy(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const r = await api.post("/stock-bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      const created = r.data?.created || 0;
      const errCount = (r.data?.errors || []).length;
      if (created > 0 && errCount === 0) {
        toast.success(`✓ ${created} stock batches uploaded successfully`);
      } else if (created > 0) {
        toast.warning(`${created} created · ${errCount} row(s) had errors`);
      } else {
        toast.error(`Upload failed · ${errCount} error(s)`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBulkBusy(false);
    }
  }

  const loadAll = async () => {
    const qsBranch = filterBranch ? `?branch_id=${filterBranch}` : "";
    const whParam = filterWarehouse ? `&warehouse_id=${filterWarehouse}` : "";
    const qsBatch = `?include_depleted=${includeDepleted}${filterBranch ? `&branch_id=${filterBranch}` : ""}`;
    const sbwUrl = `/inventory/stock-by-warehouse${qsBranch}${qsBranch ? whParam : (filterWarehouse ? `?warehouse_id=${filterWarehouse}` : "")}`;
    const [a, b, c, sbw] = await Promise.all([
      api.get(`/inventory/summary${qsBranch}`),
      api.get(`/inventory/batches${qsBatch}`),
      api.get(`/stock-entries${qsBranch}`),
      api.get(sbwUrl).catch(() => ({ data: [] })),
    ]);
    setSummary(a.data); setBatches(b.data); setEntries(c.data);
    setBatchServerTotal(parseInt(b.headers?.["x-total-count"] || "0", 10) || 0);
    setStockByWh(Array.isArray(sbw.data) ? sbw.data : []);
    // Sprint 4.7 — coverage banner. Silently ignore errors so the page works
    // even if the user doesn't have warehouse view permission.
    api.get(`/inventory/warehouse-coverage${qsBranch}`)
       .then(r => setCoverage(r.data))
       .catch(() => setCoverage(null));
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterBranch, filterWarehouse, includeDepleted]);

  // Jul-2026 — the movements list is capped at the newest 200 rows
  // server-side, so client-only search missed an item's OLDER movements
  // (live bug: 10-qty batch showed only 6 issues). When the user types a
  // search, ask the SERVER to filter so every movement of that item comes
  // back (up to 500).
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const p = new URLSearchParams();
        if (filterBranch) p.set("branch_id", filterBranch);
        if (search.trim()) { p.set("search", search.trim()); p.set("limit", "500"); }
        const r = await api.get(`/stock-entries?${p.toString()}`);
        setEntries(r.data);
      } catch { /* keep previous rows */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Sprint 4.3 — Warehouse picker options follow the branch filter.
  // Reset Warehouse filter if branch changes (it may now be out of scope).
  useEffect(() => {
    const url = filterBranch ? `/warehouses/picker?branch_id=${filterBranch}` : `/warehouses/picker`;
    api.get(url).then(r => setWarehouseOptions(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWarehouseOptions([]));
    setFilterWarehouse("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  useEffect(() => {
    api.get("/settings/inventory")
      .then(r => setAllowNegativeStock(!!r.data?.allow_negative_stock))
      .catch(() => {});
  }, [settingsOpen]);

  // All unique item categories across summary + batches
  const categories = useMemo(() => {
    const s = new Set();
    summary.forEach(x => x.item_group && s.add(x.item_group));
    batches.forEach(x => x.item_group && s.add(x.item_group));
    return Array.from(s).sort();
  }, [summary, batches]);

  // Filtering + sorting pipelines
  function matchesSearch(row) {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (row.item_name || "").toLowerCase().includes(q) ||
      (row.item_code || "").toLowerCase().includes(q) ||
      (row.batch_code || "").toLowerCase().includes(q) ||
      (row.vendor_name || "").toLowerCase().includes(q) ||
      (row.source_pi_number || "").toLowerCase().includes(q)
    );
  }
  function matchesCategory(row) {
    if (!filterCategory) return true;
    return (row.item_group || "Ungrouped") === filterCategory;
  }

  // Summary: filter + group / sort
  const summaryView = useMemo(() => {
    const rows = summary.filter(matchesSearch).filter(matchesCategory);
    if (groupByCategory) {
      rows.sort((a, b) => {
        const ca = (a.item_group || "ZZZ").toLowerCase();
        const cb = (b.item_group || "ZZZ").toLowerCase();
        if (ca !== cb) return ca.localeCompare(cb);
        return (a.item_name || "").toLowerCase().localeCompare((b.item_name || "").toLowerCase());
      });
    } else {
      rows.sort((a, b) => (a.item_name || "").toLowerCase().localeCompare((b.item_name || "").toLowerCase()));
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, search, filterCategory, groupByCategory]);

  // Sprint 4.3 — Warehouse-roll-up current stock with enrichment for category etc.
  const stockByWhView = useMemo(() => {
    // Map item_id → category from the existing summary list so we can group
    // by category in the warehouse view too.
    const catByItemId = {};
    summary.forEach(s => { if (s.item_id) catByItemId[s.item_id] = s.item_group || "Ungrouped"; });
    let rows = stockByWh.map(r => ({
      ...r,
      item_group: catByItemId[r.item_id] || "Ungrouped",
      current_stock: r.total_qty,
      batch_count: r.batch_count,
      rate: r.total_qty > 0 ? (r.total_value / r.total_qty) : 0,
      value: r.total_value,
    }));
    rows = rows.filter(matchesSearch).filter(matchesCategory);
    if (groupByCategory) {
      rows.sort((a, b) => {
        const ca = (a.item_group || "ZZZ").toLowerCase();
        const cb = (b.item_group || "ZZZ").toLowerCase();
        if (ca !== cb) return ca.localeCompare(cb);
        return (a.item_name || "").toLowerCase().localeCompare((b.item_name || "").toLowerCase());
      });
    } else {
      rows.sort((a, b) => (a.item_name || "").toLowerCase().localeCompare((b.item_name || "").toLowerCase()));
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockByWh, summary, search, filterCategory, groupByCategory]);

  const batchesView = useMemo(() => {
    let rows = batches.filter(matchesSearch).filter(matchesCategory);
    if (filterWarehouse) {
      rows = rows.filter(b => (b.warehouse_id || "") === filterWarehouse);
    }
    if (groupByCategory) {
      rows.sort((a, b) => {
        const ca = (a.item_group || "ZZZ").toLowerCase();
        const cb = (b.item_group || "ZZZ").toLowerCase();
        if (ca !== cb) return ca.localeCompare(cb);
        return (a.received_date || "").localeCompare(b.received_date || "");
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches, search, filterCategory, groupByCategory, filterWarehouse]);

  const entriesView = useMemo(() => {
    let rows = entries.filter(matchesSearch);
    if (filterWarehouse) {
      rows = rows.filter(e => (e.warehouse_id || "") === filterWarehouse);
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, search, filterWarehouse]);

  const totalValue = useMemo(() => stockByWhView.reduce((a, b) => a + (b.value || 0), 0), [stockByWhView]);

  // Sprint 4.5+ — client-side pagination for the heavy Batches + Stock Entries
  // tabs AND the Current Stock (summary) tab. Even though Summary collapses to
  // one row per item × warehouse, real tenants can still hit hundreds of rows.
  const summaryPg = useClientPagination(stockByWhView, { initialPageSize: 50 });
  const batchesPg = useClientPagination(batchesView, { initialPageSize: 50 });
  const entriesPg = useClientPagination(entriesView, { initialPageSize: 50 });

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Inventory"
        title="Stock Management"
        subtitle="Auto-tracked from Purchase Invoice approval — batch-wise (FIFO), per branch."
        actions={
          <>
            <button data-testid="export-stock-xlsx" onClick={() => downloadReport("stock", "xlsx")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button data-testid="export-stock-pdf" onClick={() => downloadReport("stock", "pdf")} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <FileText size={14} /> PDF
            </button>
            {isAdmin && (
              <button data-testid="bulk-assign-warehouse-btn"
                onClick={() => setBulkAssignOpen(true)}
                title="Bulk-tag existing inventory / asset records to a warehouse"
                className="bg-white border border-slate-300 px-3 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                <WarehouseIcon size={14} /> Assign Warehouse
              </button>
            )}
            {isAdmin && (
              <button data-testid="inventory-settings-btn" onClick={() => setSettingsOpen(true)}
                className="bg-white border border-slate-300 px-3 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2" title="Inventory Settings">
                <Settings size={14} />
              </button>
            )}
            <button data-testid="bulk-upload-stock-btn" onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkFile(null); }}
              className="bg-white border border-[#F97316] text-[#F97316] px-4 py-2.5 text-xs uppercase tracking-wider font-bold hover:bg-[#FFF4EC] flex items-center gap-2"
              title="Upload an Excel file to add multiple stock batches at once">
              <Upload size={14} /> Bulk Upload
            </button>
            <button data-testid="add-stock-entry-btn" onClick={() => setOpen(true)} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> Manual Entry
            </button>
          </>
        }
      />

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="inventory-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search item / batch / vendor / PI…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[300px]" />
        </div>
        <select data-testid="inventory-category-filter" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">All item groups</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="inventory-branch-filter" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse</span>
        <select data-testid="inventory-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm min-w-[180px]">
          <option value="">All warehouses</option>
          {warehouseOptions.map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input data-testid="group-by-category" type="checkbox" checked={groupByCategory} onChange={e => setGroupByCategory(e.target.checked)} className="accent-[#F97316]" />
          Group by item group
        </label>
        {tab === "batches" && (
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input data-testid="include-depleted-toggle" type="checkbox" checked={includeDepleted} onChange={e => setIncludeDepleted(e.target.checked)} className="accent-[#F97316]" />
            Include depleted batches
          </label>
        )}
        <div className="ml-auto text-xs text-slate-500 flex items-center gap-4">
          <span><span className="text-slate-400 mr-1">Items in stock:</span><span className="font-bold text-slate-800 tabular">{stockByWhView.length}</span></span>
          <span><span className="text-slate-400 mr-1">Total value:</span><span className="font-bold text-[#F97316] tabular">{formatINR(totalValue)}</span></span>
        </div>
      </div>

      {/* Sprint 4.7 — coverage banner: surfaces when legacy data lacks a
          warehouse_id so the operator can fix it in one click. */}
      {coverage && isAdmin && (coverage.inventory?.missing > 0 || coverage.assets?.missing > 0) && (
        <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-3 flex items-center gap-3 flex-wrap"
             data-testid="warehouse-coverage-banner">
          <ShieldAlert size={16} className="text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800">Warehouse tagging incomplete</div>
            <div className="text-xs text-amber-900 mt-0.5">
              {coverage.inventory.missing > 0 && (
                <span><strong className="tabular-nums">{coverage.inventory.missing}</strong> of {coverage.inventory.total} batches</span>
              )}
              {coverage.inventory.missing > 0 && coverage.assets.missing > 0 && <span className="mx-1">·</span>}
              {coverage.assets.missing > 0 && (
                <span><strong className="tabular-nums">{coverage.assets.missing}</strong> of {coverage.assets.total} assets</span>
              )}
              <span className="ml-1 text-amber-700">have no warehouse linked.</span>
            </div>
          </div>
          <button data-testid="warehouse-coverage-banner-fix"
            onClick={() => setBulkAssignOpen(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <WarehouseIcon size={12} /> Fix it now
          </button>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {[["summary", "Current Stock"], ["batches", "Batches"], ["entries", "Movements"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      <div className="bg-amber-50/60 border border-amber-200 px-4 py-2 mb-3 flex items-start gap-2">
        <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-900">
          Inventory items are <strong>auto-received as batches</strong> when a Purchase Invoice is approved.
          Asset items become individual <strong>Asset records</strong> with auto-generated codes (<code className="font-mono">AST/&lt;ITEM&gt;-NNN</code>, prefix configurable in <em>Settings → Numbering Series → Asset</em>).
          Use the <em>Manual Entry</em> action only for opening stock, adjustments, or non-PI movements.
        </p>
      </div>

      {allowNegativeStock && (
        <div role="alert" data-testid="negative-stock-banner"
          className="bg-red-50 border border-red-300 px-4 py-2 mb-3 flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-red-700 text-white px-2 py-0.5">Negative Stock ON</span>
          <p className="text-xs text-red-900 flex-1">
            Issue Vouchers can over-issue beyond available qty — batches may go <strong>negative</strong> until reconciled by a PI.
          </p>
          {isAdmin && (
            <button onClick={() => setSettingsOpen(true)} className="text-[10px] uppercase tracking-wider font-bold text-red-800 hover:underline">
              Manage →
            </button>
          )}
        </div>
      )}

      {tab === "summary" && (
        stockByWhView.length === 0 ? <EmptyState title="No stock data" /> : (
          <Card className="overflow-hidden">
            <table className="w-full" data-testid="stock-summary-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Item", "Item Group", "Branch", "Warehouse", "Unit", "Physical", "Reserved", "Available", "Batches", "Avg Rate", "Stock Value"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-5">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(() => {
                  // Render the paged slice. When group-by-category is on, we
                  // inject a header row whenever the category changes — including
                  // the very first row of each page so headers stay anchored.
                  const startIdx = (summaryPg.page - 1) * summaryPg.pageSize;
                  let prevCat = summaryPg.page > 1
                    ? (stockByWhView[startIdx - 1]?.item_group || "Ungrouped")
                    : null;
                  return summaryPg.paged.map((s, i) => {
                    const cat = s.item_group || "Ungrouped";
                    const showHeader = groupByCategory && cat !== prevCat;
                    prevCat = cat;
                    const rowKey = `${s.item_id}-${s.branch_id}-${s.warehouse_id}-${startIdx + i}`;
                    return (
                      <React.Fragment key={rowKey}>
                        {showHeader && (
                          <tr className="bg-slate-100/70">
                            <td colSpan={11} className="py-1.5 px-5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">
                              <Layers size={11} className="inline mr-1.5 -mt-0.5" />{cat}
                            </td>
                          </tr>
                        )}
                        <SummaryRow s={s} />
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
            <PaginationControls page={summaryPg.page} pageSize={summaryPg.pageSize} total={summaryPg.total}
              onPage={summaryPg.setPage} onPageSize={summaryPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        )
      )}

      {tab === "batches" && batchServerTotal > batches.length && (
        <div className="flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-800 text-xs font-semibold px-4 py-2.5 mb-4"
          data-testid="batches-truncation-warning">
          <AlertTriangle size={14} className="shrink-0" />
          Showing {batches.length.toLocaleString()} of {batchServerTotal.toLocaleString()} batches — some batches are hidden.
          Narrow the branch/warehouse filter or contact support; transactions on hidden batches may fail.
        </div>
      )}
      {tab === "batches" && (
        batchesView.length === 0 ? <EmptyState title="No batches yet" icon={Boxes} /> : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="batches-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Batch Code", "Received", "Item", "Item Group", "Branch", "Warehouse", "Vendor", "Received Qty", "Remaining", "Rate", "Value", "Source PI"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {batchesPg.paged.map(b => {
                    const depleted = b.remaining_qty <= 0;
                    const negative = b.remaining_qty < 0;
                    return (
                      <tr key={b.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${depleted && !negative ? "opacity-50" : ""} ${negative ? "bg-red-50/40" : ""}`}>
                        <td className="py-3 px-4 text-xs font-mono font-bold text-slate-900">{b.batch_code}</td>
                        <td className="py-3 px-4 text-xs text-slate-500">{(b.received_date || "").slice(0, 10)}</td>
                        <td className="py-3 px-4 text-sm text-slate-800">{b.item_name}<div className="text-[10px] text-slate-400 font-mono">{b.item_code}</div></td>
                        <td className="py-3 px-4 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{b.item_group || "—"}</td>
                        <td className="py-3 px-4 text-xs text-slate-600">{b.branch_name || "—"}</td>
                        <td className="py-3 px-4 text-xs text-slate-700" data-testid={`batch-wh-${b.id}`}>
                          {b.warehouse_name
                            ? <span className="font-bold">{b.warehouse_name}</span>
                            : <span className="text-amber-600 font-bold">— missing —</span>}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-600">{b.vendor_name || "—"}</td>
                        <td className="py-3 px-4 text-sm tabular">{b.received_qty} {b.unit}</td>
                        <td className={`py-3 px-4 text-sm tabular font-bold ${negative ? "text-red-700" : "text-emerald-700"}`}>{b.remaining_qty} {b.unit}</td>
                        <td className="py-3 px-4 text-sm tabular">{formatINR(b.unit_rate)}</td>
                        <td className="py-3 px-4 text-sm tabular font-bold text-[#F97316]">{formatINR(b.value)}</td>
                        <td className="py-3 px-4 text-xs text-slate-500">{b.source_pi_number || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationControls page={batchesPg.page} pageSize={batchesPg.pageSize} total={batchesPg.total}
              onPage={batchesPg.setPage} onPageSize={batchesPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        )
      )}

      {tab === "entries" && (
        entriesView.length === 0 ? <EmptyState title="No stock movements" /> : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Date", "Type", "Item", "Qty", "Branch", "Warehouse", "Batch", "Project", "Vendor", "Issued To", "Ref"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {entriesPg.paged.map(e => (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="py-3 px-4 text-xs text-slate-600">{(e.entry_date || "").slice(0, 10)}</td>
                      <td className="py-3 px-4">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${
                          e.entry_type === "IN" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                          e.entry_type === "ISSUE" ? "bg-orange-50 text-orange-800 border-orange-200" :
                          e.entry_type === "RETURN" ? "bg-blue-50 text-blue-800 border-blue-200" :
                          "bg-red-50 text-red-700 border-red-200"
                        }`}>{e.entry_type}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-800">{e.item_name}</td>
                      <td className="py-3 px-4 text-sm tabular font-bold">{e.quantity} {e.unit}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{e.branch_name || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-700" data-testid={`entry-wh-${e.id}`}>
                        {e.warehouse_name
                          ? <span className="font-bold">{e.warehouse_name}</span>
                          : <span className="text-amber-600">—</span>}
                      </td>
                      <td className="py-3 px-4 text-[10px] font-mono text-slate-500">{e.batch_code || "—"}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{e.project_name || "—"}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{e.vendor_name || "—"}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{e.issued_to || "—"}</td>
                      <td className="py-3 px-4 text-xs text-slate-500">
                        {/* Backend computes `ref_display` — renders
                            "Manual Added" for operator-entered manual
                            stock rows, the source voucher number for
                            system-generated ones, "—" otherwise. */}
                        {e.ref_display || e.reference_no || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={entriesPg.page} pageSize={entriesPg.pageSize} total={entriesPg.total}
              onPage={entriesPg.setPage} onPageSize={entriesPg.setPageSize}
              pageSizeOptions={[25, 50, 100, 200]} />
          </Card>
        )
      )}

      {open && <StockModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); loadAll(); }} />}
      {bulkAssignOpen && (
        <BulkAssignWarehouseModal
          mode="inventory"
          defaultBranchId={filterBranch || ""}
          onClose={() => setBulkAssignOpen(false)}
          onDone={() => { setBulkAssignOpen(false); loadAll(); }}
        />
      )}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      {bulkOpen && (
        <BulkUploadModal
          onClose={() => setBulkOpen(false)}
          file={bulkFile}
          setFile={setBulkFile}
          busy={bulkBusy}
          result={bulkResult}
          onDownload={downloadStockTemplate}
          onUpload={uploadStockFile}
        />
      )}
    </div>
  );
}

function SummaryRow({ s }) {
  const [expandedReserved, setExpandedReserved] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [loadingReserv, setLoadingReserv] = useState(false);
  const reserved  = Number(s.reserved_qty || 0);
  const physical  = Number(s.current_stock || s.total_qty || 0);
  const available = Number(s.available_qty ?? Math.max(physical - reserved, 0));

  async function loadReservationDetails() {
    if (reservationDetails) { setExpandedReserved(v => !v); return; }
    setLoadingReserv(true);
    try {
      const { data } = await api.get(`/inventory/reservations`, {
        params: { item_id: s.item_id, warehouse_id: s.warehouse_id || "" },
      });
      setReservationDetails(data);
      setExpandedReserved(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reservation details");
    } finally { setLoadingReserv(false); }
  }

  return (
    <>
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`summary-row-${s.item_id}-${s.warehouse_id || "none"}`}>
      <td className="py-3 px-5 text-sm font-medium text-slate-800">{s.item_name}<div className="text-[10px] text-slate-400 font-mono">{s.item_code}</div></td>
      <td className="py-3 px-5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{s.item_group || "—"}</td>
      <td className="py-3 px-5 text-xs text-slate-600">{s.branch_name || "—"}</td>
      <td className="py-3 px-5 text-xs text-slate-700" data-testid={`summary-wh-${s.item_id}-${s.warehouse_id || "none"}`}>
        {s.warehouse_name
          ? <span className="font-bold">{s.warehouse_name}</span>
          : <span className="text-amber-600">— missing —</span>}
      </td>
      <td className="py-3 px-5 text-sm text-slate-600">{s.unit}</td>
      {/* Physical */}
      <td className={`py-3 px-5 text-sm tabular font-bold ${physical < 0 ? "text-red-700" : "text-slate-900"}`}
          data-testid={`summary-physical-${s.item_id}`}>
        {physical}
      </td>
      {/* Reserved — clickable when > 0 */}
      <td className="py-3 px-5 text-sm tabular"
          data-testid={`summary-reserved-${s.item_id}`}>
        {reserved > 0 ? (
          <button onClick={loadReservationDetails}
                  data-testid={`summary-reserved-btn-${s.item_id}`}
                  className="font-bold text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1">
            {reserved}
            <ChevronDown size={11} className={`transition-transform ${expandedReserved ? "rotate-180" : ""}`} />
          </button>
        ) : <span className="text-slate-400">0</span>}
        {/* Mobile-visible badge: "Reserved: 30" shown only on <sm screens.
            On desktop the dedicated column is enough. */}
        <span className="sm:hidden inline-block ml-1 text-[9px] uppercase tracking-wider text-slate-500">rsvd</span>
      </td>
      {/* Available */}
      <td className={`py-3 px-5 text-sm tabular font-bold ${available <= 0 ? "text-red-700" : "text-emerald-700"}`}
          data-testid={`summary-available-${s.item_id}`}>
        {available}
      </td>
      <td className="py-3 px-5 text-xs text-slate-600">{s.batch_count}</td>
      <td className="py-3 px-5 text-sm tabular text-slate-700">{formatINR(s.rate)}</td>
      <td className="py-3 px-5 text-sm tabular font-bold text-[#F97316]">{formatINR(s.value)}</td>
    </tr>
    {expandedReserved && (
      <tr className="bg-amber-50/50">
        <td colSpan={11} className="px-5 py-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-2">
            Reserved by pending vouchers ({reservationDetails?.count || 0})
          </div>
          {loadingReserv ? (
            <div className="text-xs text-slate-600">Loading…</div>
          ) : reservationDetails?.items?.length ? (
            <table className="w-full text-xs" data-testid={`reservation-detail-table-${s.item_id}`}>
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                  <th className="text-left py-1">Type</th>
                  <th className="text-left py-1">Voucher</th>
                  <th className="text-left py-1">Date</th>
                  <th className="text-left py-1">Status</th>
                  <th className="text-left py-1">Counterparty</th>
                  <th className="text-right py-1">Reserved Qty</th>
                  <th className="text-left py-1">Raised By</th>
                </tr>
              </thead>
              <tbody>
                {reservationDetails.items.map((r, i) => (
                  <tr key={i} className="border-t border-amber-100">
                    <td className="py-1.5 font-bold">{r.kind}</td>
                    <td className="py-1.5 font-mono">{r.voucher_number}</td>
                    <td className="py-1.5">{r.voucher_date}</td>
                    <td className="py-1.5">
                      <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        r.status === "Pending" ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-800"
                      }`}>{r.status}</span>
                    </td>
                    <td className="py-1.5 text-slate-700">{r.counterparty}</td>
                    <td className="py-1.5 text-right tabular font-bold">{r.reserved_qty} {r.unit}</td>
                    <td className="py-1.5 text-slate-600"><UserLabel value={r.raised_by} fallback="—" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-xs text-slate-500">No pending vouchers found.</div>
          )}
        </td>
      </tr>
    )}
    </>
  );
}

function SettingsModal({ onClose }) {
  useUserDirectory();
  const [settings, setSettings] = useState({ allow_negative_stock: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/inventory").then(r => { setSettings(r.data || {}); setLoading(false); });
  }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/inventory", { allow_negative_stock: settings.allow_negative_stock });
      setSettings(data);
      toast.success("Inventory settings saved");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Settings size={18} /> Inventory Settings</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
            <>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="allow-negative-stock-toggle" type="checkbox" checked={!!settings.allow_negative_stock}
                  onChange={e => setSettings({ ...settings, allow_negative_stock: e.target.checked })}
                  className="accent-[#F97316] mt-1" />
                <div>
                  <div className="text-sm font-bold text-slate-900">Allow Negative Stock Issue</div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    When enabled, Issue Vouchers can be saved even if the chosen batch
                    has insufficient remaining qty. Batches may go <strong className="text-red-700">negative</strong> until reconciled
                    by a Purchase Invoice. Use only for emergency / out-of-stock situations.
                  </p>
                </div>
              </label>
              {settings.allow_negative_stock && (
                <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                  ⚠ Negative stock mode is <strong>ON</strong>. The qty guard on Issue Vouchers is bypassed system-wide.
                </div>
              )}
              {settings.updated_at && (
                <div className="text-[10px] text-slate-400 pt-2">
                  Last updated {settings.updated_at.slice(0, 19).replace("T", " ")} by <UserLabel value={settings.updated_by} fallback="—" />
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
          <button data-testid="save-inventory-settings-btn" onClick={save} disabled={saving || loading} className="px-5 py-2 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StockModal({ onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({
    entry_type: "IN", item_id: "", quantity: 0, unit_rate: 0,
    project_id: "", vendor_id: "",
    branch_id: "", branch_name: "",
    warehouse_id: "", warehouse_name: "",
    issued_to: "", reference_no: "", remarks: "",
  });
  const [saving, setSaving] = useState(false);
  // Sprint 2026-Q1 — typeahead state for the searchable item picker.
  // `null` means "use the bound display label"; a string means the
  // operator is mid-typing (don't override their input).
  const [stockItemSearch, setStockItemSearch] = useState(null);

  useEffect(() => {
    // Sprint 2026-Feb — switched Project & Branch to the lightweight
    // *-picker endpoints. `/projects-picker` shaves ~80% off the
    // response size, and `/branches-picker` ~45%. Cuts modal open-time
    // noticeably on tenants with hundreds of projects. `/vendors/picker`
    // was already lightweight.
    Promise.all([
      api.get("/items/_pickable"),
      api.get("/projects-picker"),
      api.get("/vendors/picker"),
      api.get("/branches-picker"),
    ]).then(([i, p, v, b]) => {
      setItems(i.data);
      setProjects(p.data);
      setVendors(v.data);
      setBranches(b.data || []);
    });
  }, []);

  // Sprint 4.3 — Branch → Warehouse cascade.
  // Auto-picks branch's default warehouse on first load + branch change.
  useEffect(() => {
    if (!form.branch_id) { setWarehouses([]); return; }
    api.get(`/warehouses/picker?branch_id=${form.branch_id}`).then(r => {
      const wlist = Array.isArray(r.data) ? r.data : [];
      setWarehouses(wlist);
      const cur = wlist.find(x => x.id === form.warehouse_id);
      if (!cur) {
        const dflt = wlist.find(x => x.is_default) || wlist[0];
        setForm(p => ({ ...p,
          warehouse_id:   dflt?.id || "",
          warehouse_name: dflt?.warehouse_name || "",
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.branch_id]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  async function save(e) {
    e.preventDefault();
    if (!form.item_id) { toast.error("Select an item"); return; }
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    if (!form.warehouse_id) { toast.error("Warehouse is mandatory. Pick a warehouse for the selected branch."); return; }
    setSaving(true);
    const item = items.find(i => i.id === form.item_id);
    const project = projects.find(p => p.id === form.project_id);
    const vendor = vendors.find(v => v.id === form.vendor_id);
    const branch = branches.find(b => b.id === form.branch_id);
    const payload = {
      ...form,
      quantity: Number(form.quantity),
      unit_rate: Number(form.unit_rate || 0),
      item_name: item?.name || "",
      unit: item?.unit || "",
      branch_name: branch?.branch_name || "",
      project_name: project?.project_name || "",
      vendor_name: vendor?.name || "",
    };
    try {
      await api.post("/stock-entries", payload);
      toast.success("Stock entry created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="font-display text-xl font-bold">Manual Stock Entry</h2>
            <p className="text-xs text-slate-500 mt-0.5">Use only for opening stock, adjustments or non-PI movements.</p>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Entry Type *">
            <select data-testid="stock-entry-type" value={form.entry_type} onChange={e => set("entry_type", e.target.value)} className={inputCls}>
              {ENTRY_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Branch *">
            <select data-testid="stock-branch" required value={form.branch_id}
              onChange={e => set("branch_id", e.target.value)} className={inputCls}>
              <option value="">Select branch…</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </Field>
          <Field label="Warehouse *">
            <select data-testid="stock-warehouse" required disabled={!form.branch_id}
              value={form.warehouse_id}
              onChange={e => {
                const w = warehouses.find(x => x.id === e.target.value);
                setForm(p => ({ ...p,
                  warehouse_id: e.target.value, warehouse_name: w?.warehouse_name || "",
                }));
              }}
              className={inputCls}>
              <option value="">
                {form.branch_id ? (warehouses.length ? "Select warehouse…" : "No warehouse available") : "Pick a branch first"}
              </option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Item *">
            {(() => {
              // Manual Stock Entry only tracks Inventory items — Services
              // and Assets are intentionally excluded (Assets have their own
              // master, Services have no physical movement). Filter the
              // master client-side so the typeahead stays a single source
              // of truth on the picker payload. Skip rows with no code AND
              // no name (master-data hygiene gap — would render as
              // "undefined · undefined" in the datalist otherwise).
              const inventoryItems = items.filter(
                (i) =>
                  (i.item_type || "").toLowerCase() === "inventory" &&
                  ((i.item_code || "").trim() || (i.name || "").trim()),
              );
              const itemLabel = (i) =>
                `${i.item_code} · ${i.name}${i.unit ? ` (${i.unit})` : ""}`;
              const selected = inventoryItems.find((i) => i.id === form.item_id);
              const displayValue =
                stockItemSearch !== null
                  ? stockItemSearch
                  : selected
                  ? itemLabel(selected)
                  : "";
              return (
                <>
                  <input
                    data-testid="stock-item"
                    type="text"
                    list="stock-item-datalist"
                    required
                    value={displayValue}
                    placeholder={`Search by code or name (${inventoryItems.length} inventory items)…`}
                    onChange={(e) => {
                      const raw = e.target.value || "";
                      setStockItemSearch(raw);
                      // When the operator picks a value from the datalist
                      // the browser fills the input with the visible
                      // label — try to match that back to an item id.
                      const lc = raw.trim().toLowerCase();
                      const exact = inventoryItems.find(
                        (i) => itemLabel(i).toLowerCase() === lc,
                      );
                      if (exact) {
                        set("item_id", exact.id);
                        setStockItemSearch(null);
                      } else if (!lc) {
                        set("item_id", "");
                      }
                    }}
                    onBlur={() => setStockItemSearch(null)}
                    className={inputCls}
                  />
                  <datalist id="stock-item-datalist">
                    {inventoryItems.map((i) => (
                      <option key={i.id} value={itemLabel(i)} />
                    ))}
                  </datalist>
                </>
              );
            })()}
          </Field>
          <Field label="Quantity *"><input data-testid="stock-qty" type="number" step="0.01" required value={form.quantity} onChange={e => set("quantity", e.target.value)} className={inputCls} /></Field>
          {form.entry_type === "IN" ? (
            <Field label="Unit Rate (₹) *">
              <input data-testid="stock-rate" type="number" step="0.01" min="0" required={form.entry_type === "IN"}
                value={form.unit_rate} onChange={e => set("unit_rate", e.target.value)}
                placeholder="0.00" className={inputCls} />
            </Field>
          ) : (
            <Field label="Unit Rate (₹)">
              <input data-testid="stock-rate" type="number" step="0.01" min="0"
                value={form.unit_rate} onChange={e => set("unit_rate", e.target.value)}
                placeholder="Optional — usually picked from source batch"
                className={inputCls} />
            </Field>
          )}
          <div className="md:col-span-2 -mt-1">
            <div className="bg-slate-50 border border-slate-200 px-4 py-2 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                Total Amount (Qty × Rate)
              </span>
              <span data-testid="stock-total-amount"
                className="text-base font-bold tabular text-[#F97316]">
                ₹ {(Number(form.quantity || 0) * Number(form.unit_rate || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <Field label="Project">
            <SearchableSelect
              value={form.project_id}
              onChange={(id) => set("project_id", id || "")}
              items={projects}
              getKey={(p) => p.id}
              getLabel={(p) => `${p.project_code || ""} · ${p.project_name || ""}`}
              getSubLabel={(p) => p.branch_name || ""}
              getSearchText={(p) => `${p.project_code || ""} ${p.project_name || ""} ${p.branch_name || ""}`}
              placeholder="Select project…"
              searchPlaceholder="Search by code, name, branch…"
              testid="stock-entry-project"
            />
          </Field>
          {form.entry_type === "IN" && (
            <Field label="Vendor">
              <SearchableSelect
                value={form.vendor_id}
                onChange={(id) => set("vendor_id", id || "")}
                items={vendors}
                getKey={(v) => v.id}
                getLabel={(v) => v.name || ""}
                getSubLabel={(v) => v.vendor_code || v.gst_number || ""}
                getSearchText={(v) => `${v.name || ""} ${v.vendor_code || ""} ${v.gst_number || ""}`}
                placeholder="Select vendor…"
                searchPlaceholder="Search by name, code, GST…"
                testid="stock-entry-vendor"
              />
            </Field>
          )}
          {form.entry_type === "ISSUE" && (
            <Field label="Issued To"><input value={form.issued_to} onChange={e => set("issued_to", e.target.value)} className={inputCls} /></Field>
          )}
          <Field label="Reference No"><input value={form.reference_no} onChange={e => set("reference_no", e.target.value)} className={inputCls} /></Field>
          <Field label="Remarks" full><textarea rows={2} value={form.remarks} onChange={e => set("remarks", e.target.value)} className={inputCls} /></Field>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-stock-btn" disabled={saving} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// =========================================================================
// Bulk Stock Upload — Excel template + upload + per-row error report
// =========================================================================
function BulkUploadModal({ onClose, file, setFile, busy, result, onDownload, onUpload }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-stock-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Inventory</div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Upload size={18} /> Bulk Upload Stock
            </h2>
          </div>
          <button onClick={onClose} data-testid="bulk-stock-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Step 1: download template */}
          <section className="border border-slate-200 bg-slate-50/50 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Step 1</div>
                <div className="font-bold text-slate-900 mt-0.5">Download the Excel template</div>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                  The template has the exact columns the importer expects, two sample rows,
                  and an Instructions sheet with all validation rules. Fill it with your stock
                  data and come back to upload.
                </p>
              </div>
              <button onClick={onDownload} data-testid="download-stock-template-btn"
                className="border border-slate-300 hover:border-slate-700 hover:bg-white text-slate-700 px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 whitespace-nowrap">
                <Download size={14} /> Download Template
              </button>
            </div>
          </section>

          {/* Step 2: upload file */}
          <section className="border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Step 2</div>
            <div className="font-bold text-slate-900 mt-0.5 mb-3">Upload your filled file</div>
            <div className="flex items-center gap-3 flex-wrap">
              <input type="file" accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs flex-1 min-w-[260px]"
                data-testid="bulk-stock-file-input" />
              <button onClick={onUpload} disabled={!file || busy}
                data-testid="bulk-stock-upload-btn"
                className="bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
                {busy ? "Uploading…" : (<><Upload size={14} /> Upload Stock</>)}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              Uploaded stock is directly LIVE — immediately consumable by Issue Vouchers. The
              normal PI-driven stock flow is unaffected. Max 500 rows per upload.
            </p>
          </section>

          {/* Result */}
          {result && (
            <section className="border border-slate-200 bg-emerald-50/30 p-4" data-testid="bulk-stock-result">
              <div className="grid grid-cols-3 gap-3 mb-3">
                <BulkStat label="Total Rows" value={result.total_rows || 0} tone="slate" />
                <BulkStat label="Created" value={result.created || 0} tone="emerald" />
                <BulkStat label="Errors" value={(result.errors || []).length} tone="rose" />
              </div>
              {(result.errors || []).length > 0 && (
                <div className="border border-rose-200 bg-rose-50/50 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-2">
                    Errors — fix in your Excel and re-upload the failed rows
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1 text-xs">
                    {(result.errors || []).map((e, i) => (
                      <div key={i} className="font-mono text-rose-800">
                        Row {e.row}: <span className="text-rose-900">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Close</button>
        </div>
      </div>
    </div>
  );
}

function BulkStat({ label, value, tone = "slate" }) {
  const tones = {
    slate:   "border-slate-200 bg-white text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose:    "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border ${tones[tone] || tones.slate} px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</div>
      <div className="text-xl font-black tabular mt-0.5">{value}</div>
    </div>
  );
}
